import React, { useState } from 'react';
import { StickyNote, ChevronDown, ChevronUp } from 'lucide-react';
import Chip from '../Chip';

export interface ReportNote {
  id: string;
  title?: string;
  content: string;
  author: string;
  created_at: string;
  tags?: string[];
}

interface ReportNotesSectionProps {
  notes: ReportNote[];
  entityName: string; // e.g., "HB25-1001" or "Sen. John Smith"
  className?: string;
  defaultExpanded?: boolean;
}

const ReportNotesSection: React.FC<ReportNotesSectionProps> = ({ 
  notes, 
  entityName,
  className = '',
  defaultExpanded = true
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (!notes || notes.length === 0) return null;

  return (
    <div className={`mt-4 border-t border-border-light dark:border-border-dark pt-4 ${className}`}>
      {/* Header with expand/collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 w-full text-left mb-3 hover:opacity-80 transition-opacity"
      >
        <StickyNote className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
        <h4 className="text-sm font-semibold text-text-secondary dark:text-text-dark-secondary">
          Notes for {entityName}
        </h4>
        <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
          ({notes.length})
        </span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary ml-auto" />
        ) : (
          <ChevronDown className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary ml-auto" />
        )}
      </button>

      {/* Notes content */}
      {isExpanded && (
        <div className="space-y-3">
          {notes.map((note) => (
            <div 
              key={note.id}
              className="bg-primary-50 dark:bg-primary-900/20 p-3 rounded-md border border-primary-200 dark:border-primary-800"
            >
              {/* Note title (if exists) */}
              {note.title && (
                <div className="font-medium text-text-primary dark:text-text-dark-primary mb-1 text-sm">
                  {note.title}
                </div>
              )}
              
              {/* Note content */}
              <div 
                className="text-sm text-text-secondary dark:text-text-dark-secondary break-words prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: note.content }}
              />

              {/* Note metadata */}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                <span className="font-medium">{note.author}</span>
                <span>•</span>
                <span>{new Date(note.created_at).toLocaleDateString()}</span>
                
                {/* Tags */}
                {note.tags && note.tags.length > 0 && (
                  <>
                    <span>•</span>
                    <div className="flex flex-wrap gap-1">
                      {note.tags.map((tag) => (
                        <Chip 
                          key={tag} 
                          label={tag} 
                          variant="neutral" 
                          size="xs"
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportNotesSection;

